import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';
import type { EntityTranslation } from '../data/printFormat';

/**
 * Per-language values for catalogue data — product names, category names.
 *
 * The server restricts `entityType` to a known allow-list, so this surface can't
 * be used to fill the table with types no renderer reads.
 *
 * Sending a blank `value` DELETES the row — clearing a field means "print the
 * entity's own stored name", which a stored empty string would defeat.
 */
export function createEntityTranslationService(client: AxiosInstance) {
  return {
    /**
     * Every translation of one type in one language.
     *
     * This is the shape a document render needs: translating a 200-row report
     * is one call, not 200. Pair it with {@link translationIndex}.
     */
    forType: async (entityType: string, languageCode: string): Promise<EntityTranslation[]> => {
      const res = await client.post<ObjectResponse<EntityTranslation[]>>(
        '/EntityTranslation/for-type',
        {},
        { params: { entityType, languageCode } },
      );
      return unwrapItems(res.data);
    },

    /** All translations of a single row, across languages — for the editor. */
    forEntity: async (entityType: string, entityId: string): Promise<EntityTranslation[]> => {
      const res = await client.post<ObjectResponse<EntityTranslation[]>>(
        '/EntityTranslation/for-entity',
        {},
        { params: { entityType, entityId } },
      );
      return unwrapItems(res.data);
    },

    /** Insert-or-update by (type, id, field, language). Blank values delete. */
    upsert: async (items: EntityTranslation[]): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>(
        '/EntityTranslation/upsert',
        { items },
      );
      return unwrap(res.data) ?? false;
    },

    remove: async (translation: EntityTranslation): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>(
        '/EntityTranslation/Delete',
        translation,
      );
      return unwrap(res.data) ?? false;
    },
  };
}

export type EntityTranslationService = ReturnType<typeof createEntityTranslationService>;

/**
 * Build an O(1) lookup from a `forType` result.
 *
 * Renderers resolve a value per row, so a linear scan of the translation list
 * would be quadratic over a long report. Key is `entityId|fieldName`, both
 * lower-cased to match the server's case-insensitive uniqueness.
 */
export function translationIndex(rows: EntityTranslation[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const r of rows ?? []) {
    if (r.isActive === false) continue;
    if (!r.entityId || !r.fieldName || !r.value) continue;
    map.set(`${r.entityId.toLowerCase()}|${r.fieldName.toLowerCase()}`, r.value);
  }
  return map;
}

/**
 * Resolve a translated data value with the documented fallback:
 * translation → the entity's own stored value.
 *
 * Written as a helper so every renderer falls back identically — an untranslated
 * product prints its real name rather than a blank.
 */
export function resolveTranslated(
  index: Map<string, string> | undefined,
  entityId: string | undefined,
  fieldName: string,
  storedValue: string | undefined,
): string {
  if (!index || !entityId) return storedValue ?? '';
  return index.get(`${entityId.toLowerCase()}|${fieldName.toLowerCase()}`) ?? storedValue ?? '';
}
